import { AlertTriangle } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export const PageHeader = ({ overline, title, children }) => (
  <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8 mt-fade-up">
    <div>
      {overline && <p className="mt-overline mb-2">{overline}</p>}
      <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">{title}</h1>
    </div>
    {children && <div className="flex flex-col sm:flex-row gap-3">{children}</div>}
  </div>
);

export const StatCard = ({ label, value, sub, testId, accent = "slate", delay = 0 }) => (
  <div className="mt-card p-5 sm:p-6 mt-fade-up" style={{ animationDelay: `${delay}ms` }} data-testid={testId}>
    <p className="mt-overline">{label}</p>
    <p className={`font-mono text-3xl sm:text-4xl font-bold tracking-tight mt-3 ${accent === "green" ? "text-emerald-700" : "text-slate-900"}`} data-testid={`${testId}-value`}>
      {value}
    </p>
    {sub && <p className="text-sm text-slate-500 mt-2">{sub}</p>}
  </div>
);

export const Disclaimer = ({ testId = "tax-disclaimer" }) => (
  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-amber-900 text-sm flex items-start gap-3" data-testid={testId}>
    <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5 text-amber-600" />
    <p>
      <strong>Estimates only.</strong> These figures are for recordkeeping purposes and are not professional tax advice. Please
      confirm your deductions with a qualified tax professional.
    </p>
  </div>
);

export const TypeBadge = ({ type }) => (
  <span
    className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${
      type === "business" ? "bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200" : "bg-slate-100 text-slate-600 ring-1 ring-slate-200"
    }`}
  >
    {type === "business" ? "Business" : "Personal"}
  </span>
);

export const EmptyState = ({ icon: Icon, title, text, children, testId }) => (
  <div className="mt-card p-10 sm:p-14 text-center flex flex-col items-center" data-testid={testId}>
    <span className="w-14 h-14 rounded-2xl bg-emerald-50 flex items-center justify-center mb-4">
      <Icon className="w-7 h-7 text-emerald-700" />
    </span>
    <h3 className="font-heading text-xl font-bold text-slate-900">{title}</h3>
    <p className="text-slate-500 mt-2 max-w-sm">{text}</p>
    {children && <div className="mt-6">{children}</div>}
  </div>
);

export const ConfirmDelete = ({ open, onOpenChange, title, onConfirm }) => (
  <AlertDialog open={open} onOpenChange={onOpenChange}>
    <AlertDialogContent className="rounded-2xl max-w-md" data-testid="confirm-delete-dialog">
      <AlertDialogHeader>
        <AlertDialogTitle className="font-heading text-xl">{title}</AlertDialogTitle>
        <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter className="gap-2">
        <AlertDialogCancel className="h-12 rounded-xl" data-testid="confirm-delete-cancel">
          Cancel
        </AlertDialogCancel>
        <AlertDialogAction onClick={onConfirm} className="h-12 rounded-xl bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-confirm">
          Yes, delete
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export const Field = ({ label, htmlFor, children, hint }) => (
  <div className="space-y-2">
    <label htmlFor={htmlFor} className="block text-sm font-semibold text-slate-800">
      {label}
    </label>
    {children}
    {hint && <p className="text-xs text-slate-500">{hint}</p>}
  </div>
);
